<div class="wizard-container">

  <!-- ==================== HEADER ==================== -->
  <div class="wizard-header">
    <div class="wizard-header-left">
      <button mat-icon-button (click)="cancelWizard()" class="back-btn" matTooltip="Back to Contracts">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <div class="wizard-title">
        <h2>New Contract</h2>
        <span class="wizard-subtitle">Step {{ currentStep }} of {{ totalSteps }} — {{ getStepLabel(currentStep) }}</span>
      </div>
    </div>
    <div class="wizard-header-right">
      <button mat-stroked-button (click)="cancelWizard()">Cancel</button>
    </div>
  </div>

  <!-- ==================== STEPPER ==================== -->
  <div class="wizard-stepper">
    <div class="stepper-track">
      <ng-container *ngFor="let s of [1,2,3,4,5,6]; let i = index">
        <div class="stepper-step"
             [class.active]="currentStep === s"
             [class.completed]="currentStep > s"
             [class.clickable]="s <= currentStep"
             (click)="goToStep(s)">
          <div class="step-indicator">
            <mat-icon *ngIf="currentStep > s" class="step-check">check</mat-icon>
            <span *ngIf="currentStep <= s">{{ s }}</span>
          </div>
          <span class="step-label">{{ getStepLabel(s) }}</span>
        </div>
        <div class="stepper-connector" *ngIf="i < 5" [class.filled]="currentStep > s"></div>
      </ng-container>
    </div>
  </div>

  <!-- ==================== CONTENT ==================== -->
  <div class="wizard-content">

    <!-- ========== STEP 1: SOURCE ========== -->
    <div class="wizard-step" *ngIf="currentStep === 1">
      <div class="step-header">
        <h3>Contract Source</h3>
        <p class="step-desc">Choose whether to start from a template or upload an existing DOCX contract.</p>
      </div>

      <!-- Source toggle -->
      <div class="source-toggle">
        <button class="source-option" [class.selected]="useTemplate" (click)="selectSource(true)">
          <mat-icon>description</mat-icon>
          <div>
            <strong>Use a Template</strong>
            <span>Fill a pre-built contract template</span>
          </div>
          <mat-icon class="check-icon" *ngIf="useTemplate">check_circle</mat-icon>
        </button>
        <button class="source-option" [class.selected]="!useTemplate" (click)="selectSource(false)">
          <mat-icon>upload_file</mat-icon>
          <div>
            <strong>Upload My Contract</strong>
            <span>Upload an existing DOCX file</span>
          </div>
          <mat-icon class="check-icon" *ngIf="!useTemplate">check_circle</mat-icon>
        </button>
      </div>

      <!-- Templates list -->
      <div class="templates-section" *ngIf="useTemplate">
        <h4>Select a Template</h4>
        <div *ngIf="templatesLoading" class="loading-row">
          <mat-spinner diameter="28"></mat-spinner>
          <span>Loading templates…</span>
        </div>
        <div class="template-grid" *ngIf="!templatesLoading">
          <div class="template-card"
               *ngFor="let t of templates; trackBy: trackById"
               [class.selected]="selectedTemplate?.id === t.id"
               (click)="selectTemplate(t)">
            <mat-icon class="template-icon">article</mat-icon>
            <div class="template-info">
              <strong>{{ t.name }}</strong>
              <span *ngIf="t.contractType" class="template-type-badge">{{ t.contractType }}</span>
              <span *ngIf="t.description" class="template-desc">{{ t.description }}</span>
            </div>
            <mat-icon class="selected-check" *ngIf="selectedTemplate?.id === t.id">check_circle</mat-icon>
          </div>
          <div class="no-templates" *ngIf="templates.length === 0">
            <mat-icon>folder_open</mat-icon>
            <p>No templates found. Upload your own contract instead.</p>
          </div>
        </div>
      </div>

      <!-- Upload area -->
      <div class="upload-section" *ngIf="!useTemplate">
        <h4>Upload DOCX Contract</h4>

        <div *ngIf="!uploadedFile"
             class="upload-area"
             [class.drag-over]="isDragOver"
             (dragover)="onDragOver($event)"
             (dragleave)="onDragLeave($event)"
             (drop)="onDrop($event)"
             (click)="triggerFileInput()">
          <mat-icon class="upload-icon">cloud_upload</mat-icon>
          <p class="upload-primary">Drag & drop your DOCX here</p>
          <p class="upload-secondary">or click to browse</p>
          <span class="upload-hint">Supported: .docx files only</span>
          <input #fileInput type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                 (change)="onFileSelected($event)" style="display:none">
        </div>

        <div *ngIf="uploadedFile" class="uploaded-file-preview">
          <mat-icon class="file-icon">description</mat-icon>
          <div class="file-meta">
            <strong>{{ uploadedFile.name }}</strong>
            <span>{{ formatFileSize(uploadedFile.size) }}</span>
          </div>
          <button mat-icon-button (click)="removeUploadedFile()" matTooltip="Remove file" color="warn">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- ========== STEP 2: CONTRACT DETAILS ========== -->
    <div class="wizard-step" *ngIf="currentStep === 2">
      <div class="step-header">
        <h3>Contract Details</h3>
        <p class="step-desc">Fill in the core contract information and type-specific fields.</p>
      </div>

      <form [formGroup]="contractForm" class="contract-form">

        <!-- Core Fields: 3 columns -->
        <div class="form-section">
          <h4 class="section-title">Core Information</h4>
          <div class="form-grid-3">

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Contract Title *</mat-label>
              <input matInput formControlName="contractTitle" placeholder="e.g. Supply Agreement — Acme Corp 2026">
              <mat-error>Contract title is required (min 3 characters)</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Contract Type *</mat-label>
              <mat-select formControlName="contractType">
                <mat-option *ngFor="let t of contractTypes" [value]="t.value">{{ t.label }}</mat-option>
              </mat-select>
              <mat-error>Contract type is required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Contract Number</mat-label>
              <input matInput formControlName="contractNumber" placeholder="Auto-generated">
              <button *ngIf="!generatingNumber" mat-icon-button matSuffix (click)="generateContractNumber()" matTooltip="Generate number">
                <mat-icon>refresh</mat-icon>
              </button>
              <mat-spinner *ngIf="generatingNumber" diameter="18" matSuffix style="margin-right:8px"></mat-spinner>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Supplier / Party Name *</mat-label>
              <input matInput formControlName="supplierName" placeholder="e.g. Acme Corporation">
              <mat-error>Supplier name is required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Department *</mat-label>
              <mat-select formControlName="departmentName">
                <mat-option *ngFor="let t of departmentName" [value]="t.value">{{ t.label }}</mat-option>
              </mat-select>             
              <mat-error>Department is required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Company Name</mat-label>
              <input matInput formControlName="companyName">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Priority</mat-label>
              <mat-select formControlName="priority">
                <mat-option *ngFor="let p of priorities" [value]="p">{{ p }}</mat-option>
              </mat-select>
            </mat-form-field>

            <div class="checkbox-field">
              <mat-checkbox formControlName="confidential">Mark as Confidential</mat-checkbox>
            </div>

          </div>
        </div>

        <!-- Financial -->
        <div class="form-section">
          <h4 class="section-title">Financial</h4>
          <div class="form-grid-3">

            <mat-form-field appearance="outline">
              <mat-label>Contract Amount</mat-label>
              <input matInput type="number" formControlName="contractAmount" placeholder="0.00">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Currency</mat-label>
              <mat-select formControlName="currency">
                <mat-option *ngFor="let c of currencies" [value]="c">{{ c }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Amount in Words</mat-label>
              <input matInput formControlName="amountInWords" placeholder="Optional">
            </mat-form-field>

          </div>
        </div>

        <!-- Dates -->
        <div class="form-section">
          <h4 class="section-title">Dates</h4>
          <div class="form-grid-3">

            <mat-form-field appearance="outline">
              <mat-label>Signing Date</mat-label>
              <input matInput type="date" formControlName="signingDate">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Effective Date *</mat-label>
              <input matInput type="date" formControlName="effectiveDate">
              <mat-error>Effective date is required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Expiry Date *</mat-label>
              <input matInput type="date" formControlName="expiryDate">
              <mat-error>Expiry date is required</mat-error>
            </mat-form-field>

            <div class="checkbox-field">
              <mat-checkbox formControlName="isRenewable">Renewable</mat-checkbox>
            </div>

            <mat-form-field appearance="outline" *ngIf="contractForm.get('isRenewable')?.value">
              <mat-label>Renewal Term (months)</mat-label>
              <input matInput type="number" formControlName="renewalTermMonths">
            </mat-form-field>

            <div class="checkbox-field" *ngIf="contractForm.get('isRenewable')?.value">
              <mat-checkbox formControlName="autoRenew">Auto-Renew</mat-checkbox>
            </div>

          </div>
        </div>

        <!-- Vendor Contact -->
        <div class="form-section">
          <h4 class="section-title">Vendor Contact</h4>
          <div class="form-grid-3">

            <mat-form-field appearance="outline">
              <mat-label>Contact Person</mat-label>
              <input matInput formControlName="vendorContactPerson">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Vendor Email</mat-label>
              <input matInput type="email" formControlName="vendorEmail">
              <mat-error>Enter a valid email</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Vendor Phone</mat-label>
              <input matInput formControlName="vendorPhone">
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Vendor Address</mat-label>
              <input matInput formControlName="vendorAddress">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Vendor Registration No.</mat-label>
              <input matInput formControlName="vendorRegistrationNumber">
            </mat-form-field>

          </div>
        </div>

        <!-- Banking -->
        <div class="form-section">
          <h4 class="section-title">Banking Details</h4>
          <div class="form-grid-3">

            <mat-form-field appearance="outline">
              <mat-label>Bank Name</mat-label>
              <input matInput formControlName="bankName">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Branch</mat-label>
              <input matInput formControlName="bankBranch">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Account Number</mat-label>
              <input matInput formControlName="bankAccountNumber">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Account Name</mat-label>
              <input matInput formControlName="bankAccountName">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>SWIFT / IBAN</mat-label>
              <input matInput formControlName="swiftCode">
            </mat-form-field>

          </div>
        </div>

        <!-- Type-specific fields -->
        <div class="form-section type-specific-section" *ngIf="getTypeSpecificFields().length > 0">
          <h4 class="section-title">
            <mat-icon>tune</mat-icon>
            {{ selectedContractType }} Fields
          </h4>
          <div class="form-grid-3">
            <ng-container *ngFor="let field of getTypeSpecificFields()">
              <mat-form-field appearance="outline" *ngIf="!isTextareaField(field)">
                <mat-label>{{ fieldLabel(field) }}</mat-label>
                <input matInput [type]="isNumberField(field) ? 'number' : 'text'" [formControlName]="field">
              </mat-form-field>
              <mat-form-field appearance="outline" class="span-3" *ngIf="isTextareaField(field)">
                <mat-label>{{ fieldLabel(field) }}</mat-label>
                <textarea matInput [formControlName]="field" rows="3"></textarea>
              </mat-form-field>
            </ng-container>
          </div>
        </div>

        <!-- Internal Notes -->
        <div class="form-section">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Internal Notes</mat-label>
            <textarea matInput formControlName="internalNotes" rows="3" placeholder="Optional notes (not included in contract document)"></textarea>
          </mat-form-field>
        </div>

      </form>
    </div>

    <!-- ========== STEP 3: CONTRACT LINKING ========== -->
    <div class="wizard-step" *ngIf="currentStep === 3">
      <div class="step-header">
        <h3>Contract Linking</h3>
        <p class="step-desc">Link this contract to existing contracts to track relationships like amendments, renewals, or addendums.</p>
      </div>

      <!-- Search box -->
      <div class="link-search-box">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Search contracts to link</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input matInput
                 [value]="linkSearchTerm"
                 (input)="onLinkSearchInput($event.target['value'])"
                 placeholder="Search by contract number, title, or supplier…">
          <button mat-icon-button matSuffix *ngIf="linkSearchTerm" (click)="clearLinkSearch()">
            <mat-icon>close</mat-icon>
          </button>
        </mat-form-field>
      </div>

      <!-- Search loading -->
      <div class="loading-row" *ngIf="linkSearchLoading">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Searching…</span>
      </div>

      <!-- Search results -->
      <div class="link-results-grid" *ngIf="linkSearchResults.length > 0 && !linkSearchLoading">
        <div class="link-result-card"
             *ngFor="let c of linkSearchResults; trackBy: trackById"
             [class.already-linked]="isAlreadyLinked(c)">
          <div class="link-result-main">
            <span class="link-contract-number">{{ c.contractNumber || c.name }}</span>
            <span class="link-contract-title">{{ c.contractTitle || c.name }}</span>
            <div class="link-result-meta">
              <span class="meta-chip">{{ c.supplierName }}</span>
              <span class="meta-chip status">{{ c.contractStatus }}</span>
            </div>
          </div>
          <div class="link-result-actions">
            <mat-select class="link-type-select" [(ngModel)]="c._selectedLinkType" placeholder="Link type">
              <mat-option *ngFor="let lt of linkTypes" [value]="lt.value">{{ lt.label }}</mat-option>
            </mat-select>
            <button mat-flat-button color="primary" (click)="addLinkedContract(c, c._selectedLinkType || 'RELATED')"
                    [disabled]="isAlreadyLinked(c)">
              <mat-icon>add_link</mat-icon> Link
            </button>
          </div>
        </div>
      </div>

      <!-- No results hint -->
      <div class="empty-hint" *ngIf="linkSearchTerm.length >= 2 && !linkSearchLoading && linkSearchResults.length === 0">
        <mat-icon>search_off</mat-icon>
        <p>No contracts found matching "{{ linkSearchTerm }}"</p>
      </div>

      <!-- Linked contracts list -->
      <div class="linked-contracts-section" *ngIf="linkedContracts.length > 0">
        <h4>Linked Contracts <span class="count-badge">{{ linkedContracts.length }}</span></h4>
        <div class="linked-chips-list">
          <div class="linked-chip" *ngFor="let lc of linkedContracts; let i = index; trackBy: trackByIndex">
            <mat-icon class="chip-icon">link</mat-icon>
            <div class="chip-info">
              <strong>{{ lc.contractNumber || lc.name }}</strong>
              <span>{{ lc.contractTitle }}</span>
            </div>
            <mat-select class="chip-link-type" [(ngModel)]="lc.linkType">
              <mat-option *ngFor="let lt of linkTypes" [value]="lt.value">{{ lt.label }}</mat-option>
            </mat-select>
            <button mat-icon-button (click)="removeLinkedContract(i)" matTooltip="Remove link" color="warn">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="step-skip-hint" *ngIf="linkedContracts.length === 0 && !linkSearchTerm">
        <mat-icon>info_outline</mat-icon>
        <span>Linking is optional. You can skip this step and link contracts later.</span>
      </div>

    </div>

    <!-- ========== STEP 4: REVIEWERS & APPROVERS ========== -->
    <div class="wizard-step" *ngIf="currentStep === 4">
      <div class="step-header">
        <h3>Reviewers &amp; Approvers</h3>
        <p class="step-desc">Assign reviewers and approvers. They will be notified when the contract is submitted for review.</p>
      </div>

      <div class="reviewers-approvers-panels">

        <!-- LEFT: User Search -->
        <div class="panel user-search-panel">
          <div class="panel-header">
            <mat-icon>person_search</mat-icon>
            <h4>Find Users</h4>
          </div>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Search users</mat-label>
            <mat-icon matPrefix>search</mat-icon>
            <input matInput
                   [value]="userSearchTerm"
                   (input)="onUserSearchInput($event.target['value'])"
                   placeholder="Type a name or email…">
          </mat-form-field>

          <div class="loading-row" *ngIf="userSearchLoading">
            <mat-spinner diameter="20"></mat-spinner>
            <span>Searching…</span>
          </div>

          <div class="user-list" *ngIf="userSearchResults.length > 0 && !userSearchLoading">
            <div class="user-item" *ngFor="let user of userSearchResults; trackBy: trackById">
              <div class="user-avatar" [style.background]="getAvatarColor(user.displayName)">
                {{ getUserInitials(user.displayName) }}
              </div>
              <div class="user-info">
                <strong>{{ user.displayName }}</strong>
                <span>{{ user.email }}</span>
              </div>
              <div class="user-actions">
                <button mat-stroked-button color="primary" (click)="addAsReviewer(user)"
                        matTooltip="Add as Reviewer" [disabled]="isAlreadyReviewer(user.id)">
                  <mat-icon>rate_review</mat-icon> Reviewer
                </button>
                <button mat-stroked-button color="accent" (click)="addAsApprover(user)"
                        matTooltip="Add as Approver" [disabled]="isAlreadyApprover(user.id)">
                  <mat-icon>how_to_reg</mat-icon> Approver
                </button>
              </div>
            </div>
          </div>

          <div class="empty-hint" *ngIf="userSearchTerm.length >= 2 && !userSearchLoading && userSearchResults.length === 0">
            <mat-icon>person_off</mat-icon>
            <p>No users found</p>
          </div>

          <div class="search-prompt" *ngIf="userSearchTerm.length < 2">
            <mat-icon>manage_search</mat-icon>
            <p>Type at least 2 characters to search</p>
          </div>
        </div>

        <!-- CENTER: Reviewers -->
        <div class="panel reviewers-panel">
          <div class="panel-header">
            <mat-icon>rate_review</mat-icon>
            <h4>Reviewers</h4>
            <span class="panel-count">{{ reviewers.length }}</span>
          </div>

          <div class="panel-empty" *ngIf="reviewers.length === 0">
            <mat-icon>inbox</mat-icon>
            <p>No reviewers added</p>
          </div>

          <div class="panel-list">
            <div class="panel-item" *ngFor="let r of reviewers; let i = index; trackBy: trackById">
              <span class="order-badge">{{ r.order }}</span>
              <div class="user-avatar small" [style.background]="getAvatarColor(r.displayName)">
                {{ getUserInitials(r.displayName) }}
              </div>
              <div class="panel-item-info">
                <strong>{{ r.displayName }}</strong>
                <span>{{ r.email }}</span>
              </div>
              <mat-select [(ngModel)]="r.signatureType" class="sig-type-select">
                <mat-option *ngFor="let st of signatureTypes" [value]="st.value">{{ st.label }}</mat-option>
              </mat-select>
              <div class="order-actions">
                <button mat-icon-button (click)="moveReviewerUp(i)" [disabled]="i === 0" matTooltip="Move up">
                  <mat-icon>arrow_upward</mat-icon>
                </button>
                <button mat-icon-button (click)="moveReviewerDown(i)" [disabled]="i === reviewers.length - 1" matTooltip="Move down">
                  <mat-icon>arrow_downward</mat-icon>
                </button>
                <button mat-icon-button color="warn" (click)="removeReviewer(i)" matTooltip="Remove">
                  <mat-icon>remove_circle_outline</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Approvers -->
        <div class="panel approvers-panel">
          <div class="panel-header">
            <mat-icon>how_to_reg</mat-icon>
            <h4>Approvers</h4>
            <span class="panel-count">{{ approvers.length }}</span>
          </div>

          <div class="panel-empty" *ngIf="approvers.length === 0">
            <mat-icon>inbox</mat-icon>
            <p>No approvers added</p>
          </div>

          <div class="panel-list">
            <div class="panel-item" *ngFor="let a of approvers; let i = index; trackBy: trackById">
              <span class="order-badge">{{ a.order }}</span>
              <div class="user-avatar small" [style.background]="getAvatarColor(a.displayName)">
                {{ getUserInitials(a.displayName) }}
              </div>
              <div class="panel-item-info">
                <strong>{{ a.displayName }}</strong>
                <span>{{ a.email }}</span>
              </div>
              <mat-select [(ngModel)]="a.signatureType" class="sig-type-select">
                <mat-option *ngFor="let st of signatureTypes" [value]="st.value">{{ st.label }}</mat-option>
              </mat-select>
              <div class="order-actions">
                <button mat-icon-button (click)="moveApproverUp(i)" [disabled]="i === 0" matTooltip="Move up">
                  <mat-icon>arrow_upward</mat-icon>
                </button>
                <button mat-icon-button (click)="moveApproverDown(i)" [disabled]="i === approvers.length - 1" matTooltip="Move down">
                  <mat-icon>arrow_downward</mat-icon>
                </button>
                <button mat-icon-button color="warn" (click)="removeApprover(i)" matTooltip="Remove">
                  <mat-icon>remove_circle_outline</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div><!-- end reviewers-approvers-panels -->

      <div class="step-skip-hint" *ngIf="reviewers.length === 0 && approvers.length === 0">
        <mat-icon>info_outline</mat-icon>
        <span>Workflow is optional. The contract will be saved as <strong>Draft</strong> if no reviewers/approvers are assigned.</span>
      </div>
    </div>

    <!-- ========== STEP 5: REMINDERS ========== -->
    <div class="wizard-step" *ngIf="currentStep === 5">
      <div class="step-header">
        <h3>Reminders</h3>
        <p class="step-desc">Configure reminder notifications before the contract expires.</p>
      </div>

      <!-- Auto-generate toggle -->
      <div class="reminders-config-bar">
        <div class="auto-toggle-row">
          <mat-slide-toggle [(ngModel)]="autoGenerateReminders" (change)="onAutoGenerateToggle()">
            Auto-generate expiry reminders
          </mat-slide-toggle>
          <span class="toggle-hint" *ngIf="autoGenerateReminders">Reminders will be created at the milestone days below.</span>
        </div>

        <!-- Editable milestones -->
        <div class="milestones-section" *ngIf="autoGenerateReminders">
          <label class="milestones-label">Days before expiry:</label>
          <div class="milestones-chips">
            <div class="milestone-chip" *ngFor="let d of milestoneDays; let i = index; trackBy: trackByIndex">
              <input type="number" min="1" [value]="d"
                     (change)="updateMilestone(i, +$event.target['value'])"
                     class="milestone-input">
              <span>days</span>
              <button mat-icon-button (click)="removeMilestone(i)" *ngIf="milestoneDays.length > 1" class="milestone-remove">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <button mat-stroked-button (click)="addMilestone()" class="add-milestone-btn">
              <mat-icon>add</mat-icon> Add
            </button>
          </div>
          <button mat-stroked-button (click)="regenerateReminders()" class="regen-btn">
            <mat-icon>refresh</mat-icon> Regenerate
          </button>
        </div>
      </div>

      <!-- Reminders list -->
      <div class="reminders-section">
        <div class="reminders-list-header">
          <h4>Reminders <span class="count-badge">{{ reminders.length }}</span></h4>
          <button mat-stroked-button (click)="addCustomReminder()">
            <mat-icon>add</mat-icon> Add Custom
          </button>
        </div>

        <div class="reminders-list">
          <div class="reminder-card" *ngFor="let r of reminders; let i = index; trackBy: trackByIndex">
            <div class="reminder-header">
              <span class="reminder-type-badge" [class]="'type-' + r.reminderType.toLowerCase()">{{ r.reminderType }}</span>
              <span class="priority-badge" [class]="getReminderPriorityClass(r.priority)">{{ r.priority }}</span>
              <div class="reminder-header-actions">
                <button mat-icon-button color="warn" (click)="removeReminder(i)" matTooltip="Remove reminder">
                  <mat-icon>delete_outline</mat-icon>
                </button>
              </div>
            </div>

            <div class="reminder-body">
              <div class="reminder-row">
                <mat-form-field appearance="outline" class="reminder-field">
                  <mat-label>Purpose</mat-label>
                  <input matInput [(ngModel)]="r.purpose">
                </mat-form-field>
                <mat-form-field appearance="outline" class="reminder-field-sm">
                  <mat-label>Reminder Date</mat-label>
                  <input matInput type="date" [(ngModel)]="r.reminderDate">
                </mat-form-field>
                <mat-form-field appearance="outline" class="reminder-field-xs">
                  <mat-label>Days Before</mat-label>
                  <input matInput type="number" [(ngModel)]="r.daysBefore" min="1">
                </mat-form-field>
                <mat-form-field appearance="outline" class="reminder-field-xs">
                  <mat-label>Priority</mat-label>
                  <mat-select [(ngModel)]="r.priority">
                    <mat-option value="LOW">Low</mat-option>
                    <mat-option value="MEDIUM">Medium</mat-option>
                    <mat-option value="HIGH">High</mat-option>
                    <mat-option value="CRITICAL">Critical</mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="reminder-notify">
                <span class="notify-label">Notify:</span>
                <mat-checkbox [(ngModel)]="r.notifyOwner">Owner</mat-checkbox>
                <mat-checkbox [(ngModel)]="r.notifyReviewers">Reviewers</mat-checkbox>
                <mat-checkbox [(ngModel)]="r.notifyApprovers">Approvers</mat-checkbox>
                <mat-checkbox [(ngModel)]="r.notifyDepartment">Department Head</mat-checkbox>
              </div>

              <!-- Custom recipients -->
              <div class="custom-recipients" *ngIf="r.recipients && r.recipients.length > 0">
                <div class="recipient-chip" *ngFor="let email of r.recipients">
                  <mat-icon>email</mat-icon>
                  <span>{{ email }}</span>
                  <button mat-icon-button (click)="removeCustomRecipient(r, email)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
              <div class="add-recipient-row">
                <mat-form-field appearance="outline" class="recipient-input">
                  <mat-label>Add custom recipient email</mat-label>
                  <input matInput type="email" [(ngModel)]="customRecipientEmail"
                         (keyup.enter)="addCustomRecipient(r)" placeholder="person@example.com">
                </mat-form-field>
                <button mat-icon-button (click)="addCustomRecipient(r)" color="primary">
                  <mat-icon>person_add</mat-icon>
                </button>
              </div>

            </div>
          </div>

          <div class="empty-hint" *ngIf="reminders.length === 0">
            <mat-icon>notifications_off</mat-icon>
            <p>No reminders configured. Add a custom reminder or enable auto-generation above.</p>
          </div>
        </div>
      </div>

    </div>

    <!-- ========== STEP 6: PREVIEW & SUBMIT ========== -->
    <div class="wizard-step preview-step" *ngIf="currentStep === 6">
      <div class="step-header">
        <h3>Review &amp; Submit</h3>
        <p class="step-desc">Confirm all details before saving the contract to Alfresco.</p>
      </div>

      <!-- Success state -->
      <div class="submit-success" *ngIf="submitSuccess">
        <mat-icon class="success-icon">check_circle</mat-icon>
        <h3>Contract Saved!</h3>
        <p>{{ createdContractNumber }} has been created successfully.</p>
        <p class="redirect-hint">Redirecting to contracts list…</p>
      </div>

      <!-- Preview content -->
      <div class="preview-content" *ngIf="!submitSuccess">

        <!-- Source card -->
        <div class="preview-card">
          <div class="preview-card-header">
            <mat-icon>source</mat-icon>
            <h4>Contract Source</h4>
          </div>
          <div class="preview-item">
            <span class="preview-label">Type</span>
            <span class="preview-value">{{ useTemplate ? 'Template' : 'Upload' }}</span>
          </div>
          <div class="preview-item">
            <span class="preview-label">{{ useTemplate ? 'Template Name' : 'File Name' }}</span>
            <span class="preview-value">{{ getSourceLabel() }}</span>
          </div>
        </div>

        <!-- Core fields card -->
        <div class="preview-card">
          <div class="preview-card-header">
            <mat-icon>description</mat-icon>
            <h4>Contract Details</h4>
            <button mat-icon-button (click)="goToStep(2)" matTooltip="Edit" class="edit-btn">
              <mat-icon>edit</mat-icon>
            </button>
          </div>
          <div class="preview-items-grid">
            <div class="preview-item" *ngFor="let field of getPreviewFields()">
              <span class="preview-label">{{ field.label }}</span>
              <span class="preview-value">{{ field.value || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- Linked contracts card -->
        <div class="preview-card" *ngIf="linkedContracts.length > 0">
          <div class="preview-card-header">
            <mat-icon>link</mat-icon>
            <h4>Linked Contracts</h4>
            <button mat-icon-button (click)="goToStep(3)" matTooltip="Edit" class="edit-btn">
              <mat-icon>edit</mat-icon>
            </button>
          </div>
          <div class="linked-preview-chips">
            <div class="linked-preview-chip" *ngFor="let lc of linkedContracts">
              <strong>{{ lc.contractNumber || lc.name }}</strong>
              <span class="link-type-tag">{{ getLinkTypeLabel(lc.linkType) }}</span>
            </div>
          </div>
        </div>

        <!-- Workflow card -->
        <div class="preview-card" *ngIf="reviewers.length > 0 || approvers.length > 0">
          <div class="preview-card-header">
            <mat-icon>people</mat-icon>
            <h4>Workflow</h4>
            <button mat-icon-button (click)="goToStep(4)" matTooltip="Edit" class="edit-btn">
              <mat-icon>edit</mat-icon>
            </button>
          </div>
          <div class="workflow-preview-cols">
            <div class="workflow-preview-col" *ngIf="reviewers.length > 0">
              <h5>Reviewers ({{ reviewers.length }})</h5>
              <div class="workflow-preview-item" *ngFor="let r of reviewers">
                <div class="user-avatar tiny" [style.background]="getAvatarColor(r.displayName)">{{ getUserInitials(r.displayName) }}</div>
                <span>{{ r.displayName }}</span>
                <span class="sig-tag">{{ r.signatureType }}</span>
              </div>
            </div>
            <div class="workflow-preview-col" *ngIf="approvers.length > 0">
              <h5>Approvers ({{ approvers.length }})</h5>
              <div class="workflow-preview-item" *ngFor="let a of approvers">
                <div class="user-avatar tiny" [style.background]="getAvatarColor(a.displayName)">{{ getUserInitials(a.displayName) }}</div>
                <span>{{ a.displayName }}</span>
                <span class="sig-tag">{{ a.signatureType }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Reminders card -->
        <div class="preview-card" *ngIf="reminders.length > 0">
          <div class="preview-card-header">
            <mat-icon>notifications</mat-icon>
            <h4>Reminders ({{ reminders.length }})</h4>
            <button mat-icon-button (click)="goToStep(5)" matTooltip="Edit" class="edit-btn">
              <mat-icon>edit</mat-icon>
            </button>
          </div>
          <div class="reminders-preview-list">
            <div class="reminder-preview-row" *ngFor="let r of reminders">
              <span class="reminder-type-badge" [class]="'type-' + r.reminderType.toLowerCase()">{{ r.reminderType }}</span>
              <span class="reminder-preview-purpose">{{ r.purpose }}</span>
              <span class="reminder-preview-date">{{ r.reminderDate }}</span>
              <span class="priority-badge" [class]="getReminderPriorityClass(r.priority)">{{ r.priority }}</span>
            </div>
          </div>
        </div>

        <!-- Submit error -->
        <mat-error *ngIf="submitError" class="submit-error-msg">
          <mat-icon>error_outline</mat-icon> {{ submitError }}
        </mat-error>

      </div><!-- end preview-content -->
    </div>

  </div><!-- end wizard-content -->

  <!-- ==================== FOOTER ==================== -->
  <div class="wizard-footer">
    <button mat-stroked-button (click)="goPrev()" [disabled]="currentStep === 1 || isSubmitting">
      <mat-icon>chevron_left</mat-icon> Back
    </button>

    <div class="footer-right">
      <button mat-flat-button color="primary"
              *ngIf="currentStep < totalSteps"
              (click)="goNext()"
              [disabled]="!canProceed()">
        Next <mat-icon>chevron_right</mat-icon>
      </button>

      <button mat-flat-button color="primary"
              *ngIf="currentStep === totalSteps && !submitSuccess"
              (click)="saveContract()"
              [disabled]="isSubmitting">
        <mat-spinner diameter="18" *ngIf="isSubmitting" style="display:inline-block;margin-right:6px"></mat-spinner>
        <mat-icon *ngIf="!isSubmitting">save</mat-icon>
        {{ isSubmitting ? 'Saving…' : 'Save Contract' }}
      </button>
    </div>
  </div>

</div>